import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    Paper,
    IconButton,
    Chip,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Timeline as TimelineIcon,
    ArrowUpward as UpIcon,
    ArrowDownward as DownIcon
} from '@mui/icons-material';
import RichTextInput from './RichTextInput';

interface TimelineEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface TimelineEvent {
    id: string;
    date: string; // Could be year, date, or time
    title: string;
    description?: string;
    position: number; // Correct chronological position
}

const TimelineEditor: React.FC<TimelineEditorProps> = ({ content, onChange }) => {
    const instruction = content?.instruction || '';
    const learningContent = content?.learningContent || '';
    const events: TimelineEvent[] = content?.events || [];
    const dateFormat = content?.dateFormat || 'year'; // year, date, datetime

    const addEvent = () => {
        const newEvent: TimelineEvent = {
            id: Date.now().toString(),
            date: '',
            title: '',
            description: '',
            position: events.length + 1
        };
        onChange({ ...content, events: [...events, newEvent] });
    };

    const updateEvent = (eventId: string, field: string, value: any) => {
        const updatedEvents = events.map(event =>
            event.id === eventId ? { ...event, [field]: value } : event
        );
        onChange({ ...content, events: updatedEvents });
    };

    const deleteEvent = (eventId: string) => {
        const updatedEvents = events
            .filter(event => event.id !== eventId)
            .map((event, index) => ({ ...event, position: index + 1 }));
        onChange({ ...content, events: updatedEvents });
    };

    const moveEvent = (index: number, direction: 'up' | 'down') => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === events.length - 1)
        ) {
            return;
        }

        const newEvents = [...events];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap events
        [newEvents[index], newEvents[swapIndex]] = [newEvents[swapIndex], newEvents[index]];

        // Update positions
        const updatedEvents = newEvents.map((event, idx) => ({
            ...event,
            position: idx + 1
        }));

        onChange({ ...content, events: updatedEvents });
    };

    const getDatePlaceholder = () => {
        switch (dateFormat) {
            case 'year':
                return 'z.B. 1945';
            case 'date':
                return 'z.B. 01.01.2000';
            case 'datetime':
                return 'z.B. 01.01.2000 14:30';
            default:
                return '';
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Zeitstrahl-Aufgabe (Timeline)</Typography>
            </Box>

            <Alert severity="info">
                Lernende ordnen Ereignisse chronologisch. Die Reihenfolge, die Sie hier festlegen, ist die korrekte Zeitleiste.
            </Alert>

            {/* Learning content (optional) */}
            <RichTextInput
                label="Lerninhalt (optional)"
                value={learningContent}
                onChange={(val) => onChange({ ...content, learningContent: val })}
                helperText="Dieser formatierte Text wird im Player über dem Quiz angezeigt."
                placeholder="Optionaler Lernstoff, der über dem Quiz angezeigt wird..."
            />

            {/* Instruction */}
            <TextField
                label="Aufgabenstellung"
                fullWidth
                multiline
                rows={3}
                value={instruction}
                onChange={(e) => onChange({ ...content, instruction: e.target.value })}
                placeholder="Ordnen Sie die folgenden Ereignisse chronologisch..."
            />

            {/* Date Format */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Datums-Format</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant={dateFormat === 'year' ? 'contained' : 'outlined'}
                        onClick={() => onChange({ ...content, dateFormat: 'year' })}
                        size="small"
                    >
                        Nur Jahr (z.B. 1945)
                    </Button>
                    <Button
                        variant={dateFormat === 'date' ? 'contained' : 'outlined'}
                        onClick={() => onChange({ ...content, dateFormat: 'date' })}
                        size="small"
                    >
                        Datum (z.B. 01.01.2000)
                    </Button>
                    <Button
                        variant={dateFormat === 'datetime' ? 'contained' : 'outlined'}
                        onClick={() => onChange({ ...content, dateFormat: 'datetime' })}
                        size="small"
                    >
                        Datum & Uhrzeit
                    </Button>
                </Box>
            </Paper>

            {/* Events */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Ereignisse (chronologisch sortiert)</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addEvent}>
                        Ereignis hinzufügen
                    </Button>
                </Box>

                {events.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Ereignisse definiert
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {events.map((event, index) => (
                            <Paper key={event.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <TimelineIcon sx={{ color: 'primary.main', mt: 1 }} />
                                        <Chip label={`#${event.position}`} color="primary" size="small" />

                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <TextField
                                                    label="Datum/Jahr"
                                                    size="small"
                                                    required
                                                    value={event.date}
                                                    onChange={(e) => updateEvent(event.id, 'date', e.target.value)}
                                                    placeholder={getDatePlaceholder()}
                                                    sx={{ width: 200 }}
                                                />
                                                <TextField
                                                    label="Ereignis/Titel"
                                                    size="small"
                                                    fullWidth
                                                    required
                                                    value={event.title}
                                                    onChange={(e) => updateEvent(event.id, 'title', e.target.value)}
                                                    placeholder="z.B. Ende des 2. Weltkriegs"
                                                />
                                            </Box>

                                            <TextField
                                                label="Beschreibung (optional)"
                                                size="small"
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={event.description || ''}
                                                onChange={(e) => updateEvent(event.id, 'description', e.target.value)}
                                                placeholder="Zusätzliche Details zum Ereignis..."
                                            />
                                        </Box>

                                        {/* Move Up/Down */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <IconButton
                                                onClick={() => moveEvent(index, 'up')}
                                                disabled={index === 0}
                                                size="small"
                                                title="Früher"
                                            >
                                                <UpIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => moveEvent(index, 'down')}
                                                disabled={index === events.length - 1}
                                                size="small"
                                                title="Später"
                                            >
                                                <DownIcon />
                                            </IconButton>
                                        </Box>

                                        <IconButton
                                            onClick={() => deleteEvent(event.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Summary */}
            {events.length > 0 && (
                <Alert severity="success">
                    {events.length} Ereignis(se) definiert - Die aktuelle Reihenfolge ist chronologisch korrekt
                </Alert>
            )}

            {events.length >= 2 && (
                <Alert severity="info">
                    Frühestes: {events[0].date} • Spätestes: {events[events.length - 1].date}
                </Alert>
            )}
        </Box>
    );
};

export default TimelineEditor;
